import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { JSX } from "react";

import type { SalesHarvestContact, ShareToChatSubject } from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { ShareToChatDialog } from "@/components/share/ShareToChatDialog";

import { salesApi } from "../client.js";
import { EmailStatusBadge } from "./EmailStatusBadge.js";

export type DialogContact = SalesHarvestContact & {
  org_name?: string;
  org_id?: string;
};

/** A single harvested contact, opened as a dialog: full details, the clear
 * email-certainty badge, an on-demand "Verify now", and share actions (copy /
 * staff chat / email). */
export function ContactDialog({
  contact,
  verifierEnabled,
  onClose,
}: {
  contact: DialogContact | null;
  verifierEnabled: boolean;
  onClose: () => void;
}): JSX.Element {
  const canEdit = useCan("sales:edit");
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>(contact?.email_status ?? "unverified");
  const [copied, setCopied] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    setStatus(contact?.email_status ?? "unverified");
    setCopied(false);
  }, [contact?.id, contact?.email_status]);

  const verify = useMutation({
    mutationFn: () => salesApi.verifyContact(contact!.id),
    onSuccess: (res) => {
      setStatus(res.email_status);
      qc.invalidateQueries({ queryKey: ["sales", "harvest"] });
    },
  });

  if (!contact) return <></>;

  const detailsText = [
    contact.full_name,
    contact.title,
    contact.org_name ? `at ${contact.org_name}` : "",
    contact.email ? `Email: ${contact.email}` : "",
    contact.phone ? `Phone: ${contact.phone}` : "",
    contact.linkedin_url ? `LinkedIn: ${contact.linkedin_url}` : "",
  ].filter(Boolean).join("\n");

  const subject: ShareToChatSubject = {
    kind: "Contact",
    title: contact.full_name,
    summaryLines: [
      [contact.title, contact.org_name ? `· ${contact.org_name}` : ""].filter(Boolean).join(" "),
      contact.email ?? "",
      contact.phone ?? "",
    ].filter(Boolean),
    deepLinkPath: "/sales/contacts",
  };

  const mailto =
    `mailto:?subject=${encodeURIComponent(`Contact: ${contact.full_name}`)}` +
    `&body=${encodeURIComponent(detailsText)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(detailsText);
      setCopied(true);
    } catch {
      /* clipboard blocked — ignore */
    }
  };

  return (
    <>
      <Dialog open={Boolean(contact)} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
              {contact.full_name}
            </Typography>
            <Chip label={contact.seniority} size="small" sx={{ borderRadius: 1 }} />
          </Stack>
          {contact.title && (
            <Typography variant="body2" color="text.secondary">
              {contact.title}
              {contact.org_name ? ` · ${contact.org_name}` : ""}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Field label="Email">
              {contact.email ? (
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <Link href={`mailto:${contact.email}`} underline="hover">{contact.email}</Link>
                  <EmailStatusBadge contact={{ email: contact.email, email_status: status }} />
                  {verifierEnabled && canEdit && (
                    <Button
                      size="small"
                      startIcon={<VerifiedRoundedIcon fontSize="small" />}
                      disabled={verify.isPending}
                      onClick={() => verify.mutate()}
                    >
                      {verify.isPending ? "Verifying…" : "Verify now"}
                    </Button>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No email found.</Typography>
              )}
            </Field>
            <Field label="Phone">
              {contact.phone ? <Link href={`tel:${contact.phone}`} underline="hover">{contact.phone}</Link> : "—"}
            </Field>
            <Field label="LinkedIn">
              {contact.linkedin_url ? (
                <Link href={contact.linkedin_url} target="_blank" rel="noreferrer" underline="hover">
                  {contact.linkedin_url}
                </Link>
              ) : "—"}
            </Field>
            <Field label="Source">
              {contact.source_url ? (
                <Link href={contact.source_url} target="_blank" rel="noreferrer" underline="hover">
                  {contact.source_url}
                </Link>
              ) : "—"}
            </Field>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" startIcon={<ContentCopyRoundedIcon fontSize="small" />} onClick={copy}>
              {copied ? "Copied" : "Copy details"}
            </Button>
            <Button size="small" variant="outlined" startIcon={<ChatBubbleOutlineRoundedIcon fontSize="small" />} onClick={() => setChatOpen(true)}>
              Share to chat
            </Button>
            <Button size="small" variant="outlined" startIcon={<EmailRoundedIcon fontSize="small" />} component="a" href={mailto}>
              Email
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <ShareToChatDialog open={chatOpen} onClose={() => setChatOpen(false)} subject={subject} />
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{label}</Typography>
      <Box sx={{ flex: 1, fontSize: 14, wordBreak: "break-word" }}>{children}</Box>
    </Box>
  );
}
